// src/pages/subscription/BundleSubscribePage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionBundle, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

export const BundleSubscribePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionBundle();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    billing_account_id: '',
  });

  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetProductBundle(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePreview = async () => {
    if (!id || !formData.tenant_id) return;
    setIsLoadingPreview(true);
    try {
      const result = await store.GetBundleSubscriptionPreview(id, { tenant: { id: formData.tenant_id } } as any);
      setPreview(result);
    } catch {
      // Error handled by store
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    try {
      const result = await store.SubscribeToBundle(id, {
        tenant: { id: formData.tenant_id },
        billing_account: { id: formData.billing_account_id },
      } as any);
      const subId = result?.subscription?.entity?.id;
      navigate(subId ? `/subscriptions/bundles/subscriptions/${subId}` : '/subscriptions/bundles/subscriptions');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading && !item) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (!item && !store.isLoading) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/bundles" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const bundleItems = item?.items ?? [];

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Subscribe to Bundle</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/subscriptions/bundles" className="text-muted-foreground hover:text-foreground">Bundles</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Subscribe</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Bundle Info */}
      {item && (
        <Card>
          <CardHeader>
            <CardTitle>{String(item.name ?? '')}</CardTitle>
            {item.tagline && <CardDescription>{String(item.tagline)}</CardDescription>}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Price</p>
                <p className="text-lg font-semibold text-foreground">{formatMoney(item.bundle_price)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Discount</p>
                <p className="text-lg font-semibold text-foreground">
                  {item.discount_value ? `${item.discount_value}${item.discount_type === 'DISCOUNT_TYPE_PERCENTAGE' ? '%' : ''}` : 'None'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Cycle</p>
                <p className="text-lg font-semibold text-foreground">{formatLabel(item.billing_cycle || '', 'BILLING_CYCLE_')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Items</p>
                <p className="text-lg font-semibold text-foreground">{bundleItems.length}</p>
              </div>
            </div>
            {bundleItems.length > 0 && (
              <div className="mt-4">
                <Table striped>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bundleItems.map((bi: any, i: number) => (
                      <TableRow key={bi.entity?.id || bi.id || i}>
                        <TableCell>{String(bi.product_name ?? bi.product_id ?? '--')}</TableCell>
                        <TableCell>{String(bi.plan_name ?? bi.plan_id ?? '--')}</TableCell>
                        <TableCell>
                          <Badge variant={bi.required ? 'warning' : 'secondary'} size="sm">{bi.required ? 'Yes' : 'No'}</Badge>
                        </TableCell>
                        <TableCell>{String(bi.quantity ?? 1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscription Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Subscription Details</CardTitle>
            <CardDescription>Provide the tenant and billing account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Tenant ID" type="text" value={formData.tenant_id} onChange={(e) => handleChange('tenant_id', e.target.value)} placeholder="Tenant identifier" required />
              <Input label="Billing Account ID" type="text" value={formData.billing_account_id} onChange={(e) => handleChange('billing_account_id', e.target.value)} placeholder="Billing account identifier" required />
            </div>
            <div>
              <Button type="button" variant="secondary" onClick={handlePreview} disabled={!formData.tenant_id || isLoadingPreview}>
                {isLoadingPreview ? 'Loading Preview...' : 'Preview Subscription'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Section */}
        {preview && (
          <Card>
            <CardHeader><CardTitle>Subscription Preview</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-4 border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground">Original Price</p>
                  <p className="text-xl font-bold text-foreground">{formatMoney(preview.original_price)}</p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground">Discount Amount</p>
                  <p className="text-xl font-bold text-foreground">{formatMoney(preview.discount_amount)}</p>
                </div>
                <div className="p-4 border border-border rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Total Price</p>
                  <p className="text-xl font-bold text-foreground">{formatMoney(preview.total_price)}</p>
                </div>
              </div>
              {Array.isArray(preview.products ?? preview.items) && (preview.products ?? preview.items).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Items Breakdown</h4>
                  <Table striped>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(preview.products ?? preview.items).map((pi: any, i: number) => (
                        <TableRow key={pi.product_id || i}>
                          <TableCell>{String(pi.product_name ?? pi.product_id ?? '--')}</TableCell>
                          <TableCell>{String(pi.plan_name ?? pi.plan_id ?? '--')}</TableCell>
                          <TableCell>{String(pi.quantity ?? 1)}</TableCell>
                          <TableCell className="text-right">{formatMoney(pi.price)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Link to={`/subscriptions/bundles/${id}`}><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Subscribing...' : 'Subscribe to Bundle'}
          </Button>
        </div>
      </form>
    </div>
  );
});

export default BundleSubscribePage;
